import { defineComponent as _defineComponent } from 'vue'
import router from '@/router/router';
import { apiGet, apiPost } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { useAppStatusStore } from '@/store/appStatusStore';
import { useApplicationStore } from '@/store/applicationStore';
import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';
import WmButtonGroup from '@/app-components/WmButtonGroup.vue';
import EmailGenerator from '@/app-components/EmailGenerator.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useHelperStore } from '@/store/helperStore';
import { ColumnDef } from '@/types/ColumnDef';
import { EnquiryListItem, ProformaListItem, ProformaExpenseLine, ProformaEmailTemplate } from '@/types/ProformaTypes';
import { generateDocument } from '@/services/api/api-calls/generateDocument';
import { getAgencyProformas } from '@/services/api/api-calls/getAgencyProformas';
import { connectProformaPortcall } from '@/services/api/api-calls/connectProformaPortcall';
import { GatResponseResultCategory, GatResponseResultType } from '@/types/GatResponseResult';
import { useProformaStore } from '../stores/proformaStore';

interface ProformaListProps {
  callId: string;
  location: string;
  parentId?: number | string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProformaList',
  props: {
    callId: null,
    location: null,
    parentId: null
  },
  setup(__props: any) {

const props = __props as {
  callId: string;
  location: string;
  parentId?: number | string;
};

// eslint-disable-next-line import/no-cycle

const proformaStore = useProformaStore();
const appStatusStore = useAppStatusStore();
const applicationStore = useApplicationStore();
const portcallStore = usePortcallStore();
const helperStore = useHelperStore();

const isLoading = ref(false);
const isSaving = ref(false);
const agencyProformas = ref<EnquiryListItem[]>([]);
const portcallProformas = computed(() => proformaStore.portcallProformas);
const exportableProformaLines = ref<ProformaExpenseLine[]>([]);
const connectProformaPortcallDialogVisible = ref(false);
const exportProformaToExpenseDialogVisible = ref(false);
const selectedProformaIds = ref<number[]>([]);
const selectedImportProformaIds = ref<number[]>([]);
const selectedExportProformaLinesId = ref<number[]>([]);
const callDetails = computed(() => portcallStore.callDetails);
const emailTemplateGuid = ref<string | undefined>(undefined);
const agencyId = computed(() => callDetails.value.SETUP_ID);
const selectedProformaHasClientNumber = ref(false);
const emailTemplateItems = computed(() => {
  const templates = [...proformaStore.emailTemplates];
  let result: any[] = [];
  const ignoreGroupMinCount = applicationStore.hasFeatureCode('IGNORE_DOCUMENT_GROUPING_MIN_COUNT');
  if (Array.isArray(templates) && templates.length > 0) {
    if (ignoreGroupMinCount ? true : templates.length > 10) {
      result = helperStore.emailTemplatesGroupedByLabels(templates, false);
    } else {
      result = templates.map((item) => ({
        caption: item.TITLE,
        eventName: 'createEmailClick',
        icon: 'mdi-file-outline',
        ID: item.ID,
      }));
    }
  }
  return result;
});

const validPortcallStatusToImportExportProforma = computed(() => {
  const status = callDetails.value.STATUS;
  let callCategory;
  if (callDetails.value.extra.PCE_CALLTYPE) {
    const callType = helperStore.callTypes.find((item) => item.value == callDetails.value.extra.PCE_CALLTYPE);
    callCategory = callType?.PCT_CATEGORY;
  }

  return (status === 0 || status === 1 || status === 2 || status === 3) && !callCategory;
});

const userCanConnectProformaPortcall = computed(
  () =>
    applicationStore.user.internal &&
    (applicationStore.userRight('PR.NEW') || applicationStore.userRight('PR.NEW_FROM_PORTCALL')) &&
    validPortcallStatusToImportExportProforma.value
);

const userCanExportProforma = computed(
  () =>
    applicationStore.user.internal &&
    applicationStore.userRight('EX.NEW') &&
    validPortcallStatusToImportExportProforma.value
);

const userCanGenerateDoc = computed<boolean>(() => {
  const isInternal = applicationStore.user.internal;
  if (isInternal) {
    return applicationStore.userRight('DOC.NEW_AND_EDIT');
  }
  // const externalRights = applicationStore.userRight('DOC.GENERATE_DOCUMENT');
  // return externalRights;
  return false;
});

const selectedProformaId = computed<number>(() => {
  if (selectedProformaIds.value.length === 0) {
    return 0;
  }
  return selectedProformaIds.value[0];
});

const applicationStatus = computed(() => {
  const result = appStatusStore.getAppStatus;
  if (selectedProformaId.value) {
    result.ProformaId = selectedProformaId.value;
  }
  return result;
});

const wmButtonGroup = computed(() => {
  const result = [];
  result.push({
    caption: 'Connect proforma',
    icon: 'mdi-file-import',
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    eventName: 'prepareProformaPortcallConnectClick',
    disabled: !userCanConnectProformaPortcall.value,
  });
  result.push({
    caption: 'Export proforma',
    icon: 'mdi-file-export',
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    eventName: 'prepareExportProformaClick',
    disabled:
      !userCanExportProforma.value || selectedProformaIds.value.length !== 1 || !selectedProformaHasClientNumber.value,
  });
  result.push({
    btnType: 'CREATE_DOC',
    docType: 2,
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    disabled: !userCanGenerateDoc.value || selectedProformaId.value === 0,
  });
  result.push({
    btnType: 'CUSTOM',
    caption: 'Create email',
    icon: 'mdi-email',
    menuItems: emailTemplateItems.value,
    disabled: emailTemplateItems.value.length === 0 || selectedProformaId.value === 0,
  });
  return result;
});

const columns = computed(() => {
  const result: ColumnDef<ProformaListItem>[] = [
    { header: 'No', field: 'PROFORMA_NUMBER' },
    // { header: 'Client name', field: 'clientName' },
    // { header: 'Sum', field: 'sum', dataType: 'currency', decimals: 2 },
    { header: 'Created date', field: 'CREATED_DATE', dataType: 'date' },
    { header: 'Vessel name', field: 'VESSEL_NAME', nowrap: true },
    { header: 'Client name', field: 'CLIENT_NAME', nowrap: true },
    { header: 'Client number', field: 'CLIENT_NUMBER', nowrap: true },
    { header: 'Amount', field: 'SUBTOTAL_IN', width: '1%', dataType: 'currency', decimals: 2 },
    // {
    //   header: 'Discount',
    //   field: 'discount',
    //   dataType: 'currency',
    //   decimals: 2,
    // },
    // {
    //   header: 'Surcharge',
    //   field: 'surcharge',
    //   dataType: 'currency',
    //   decimals: 2,
    // },
    // { header: 'VAT', field: 'vat', dataType: 'currency', decimals: 2 },
    // { header: 'Total', field: 'total', dataType: 'currency', decimals: 2 },
    { header: 'Comment', width: '100%', field: 'COMMENTS_NEW' },
  ];
  return result;
});

const newProformaColumns = computed(() => {
  const result: ColumnDef<EnquiryListItem>[] = [
    { header: 'Proforma no', field: 'PROFORMA_NUMBER', nowrap: true },
    { header: 'Created date', field: 'CREATED_DATE', nowrap: true },
    { header: 'Vessel', field: 'VESSEL_NAME', nowrap: true },
    { header: 'Harbour', field: 'HNAME', nowrap: true },
    { header: 'Total', field: 'TOTAL', dataType: 'number', nowrap: true },
    { header: 'Client', field: 'CLIENT_NAME', nowrap: true },
  ];
  return result;
});

const proformaLineColumns = computed(() => {
  const result: ColumnDef<ProformaExpenseLine>[] = [
    { header: 'Text', field: 'EXPENSE_TEXT', nowrap: true },
    { header: 'Quantity', field: 'QUANTITY', dataType: 'number', nowrap: true },
    { header: 'Price', field: 'PRICE', dataType: 'number', nowrap: true },
    { header: 'Vat %', field: 'VAT', nowrap: true },
    { header: 'Total', field: 'TOTAL_SUM', dataType: 'number', nowrap: true },
    { header: 'Account code', field: 'ACCOUNT_CODE', nowrap: true },
    { header: 'Additional info', field: 'MISC', nowrap: true },
    { header: 'Budget?', field: 'BUDGET', nowrap: true },
    { header: 'Client', field: 'CLIENT_NAME', nowrap: true },
    { header: 'Supplier', field: 'SUPPLIER_NAME', nowrap: true },
    { header: 'Expense group', field: 'EXPENSE_GROUP_NAME', nowrap: true },
    { header: 'Expense template', field: 'EXPENSE_TEMPLATE_NAME', nowrap: true },
    { header: 'Price group', field: 'PRICE_GROUP_NAME', nowrap: true },
  ];
  return result;
});

function emailGeneratorIsLoading(event: boolean) {
  if (!event) {
    emailTemplateGuid.value = undefined;
  }
}

async function getProformas(portcallId: string) {
  isLoading.value = true;
  try {
    await proformaStore.fetchPortcallProformas(portcallId);
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

function proformaSelected(proformaIDs: number[]) {
  selectedProformaIds.value = [];
  if (proformaIDs.length >= 1) {
    const proformaId = proformaIDs[proformaIDs.length - 1];
    selectedProformaIds.value.push(proformaId);
    const selectedProforma = portcallProformas.value.find((proforma) => +proforma.ID === proformaId);
    if (selectedProforma) {
      selectedProformaHasClientNumber.value = !!selectedProforma.CLIENT_NUMBER;
    } else {
      selectedProformaHasClientNumber.value = false;
    }
  } else {
    selectedProformaHasClientNumber.value = false;
  }
}

function importProformaRowClass(item: EnquiryListItem) {
  return item.VESSEL_ID === callDetails.value.VESSEL_ID ? 'font-weight-bold' : '';
}

function exportProformaLineRowClass(item: ProformaExpenseLine) {
  return item.EXPORTABLE === 0 ? 'red--text' : '';
}

function importProformaSelected(proformaIDs: number[]) {
  selectedImportProformaIds.value = [];
  if (proformaIDs.length > 1) {
    selectedImportProformaIds.value.push(proformaIDs[proformaIDs.length - 1]);
  }
  if (proformaIDs.length === 1) {
    selectedImportProformaIds.value.push(proformaIDs[0]);
  }
}

function exportableProformaLineSelected(proformaLinesID: number[]) {
  selectedExportProformaLinesId.value = [];
  selectedExportProformaLinesId.value.push(...proformaLinesID);
}

function proformaClicked(rowItem: ProformaListItem) {
  const params = {
    callId: props.callId,
    proformaId: rowItem.ID,
  };
  router.push({ name: 'proformaItem', params });
}

function checkboxIsHidden(item: ProformaExpenseLine) {
  if (item.EXPORTABLE === 1) {
    return false;
  }
  return true;
}

async function prepareProformaPortcallConnect(item: any) {
  try {
    isLoading.value = true;
    agencyProformas.value = [];
    selectedImportProformaIds.value = [];
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    const fromDateTime = oneYearAgo.toISOString();
    const toDateTime = today.toISOString();
    const result = await getAgencyProformas(item.agencyId, fromDateTime, toDateTime, false);
    if (result.resultCategory === GatResponseResultCategory.Success && result.payload) {
      const unsorted = result.payload.proformas;
      const sorted = unsorted.sort((a, b) => b.PROFORMA_NUMBER.localeCompare(a.PROFORMA_NUMBER));
      agencyProformas.value = sorted;
    }
    connectProformaPortcallDialogVisible.value = true;
  } catch {
    connectProformaPortcallDialogVisible.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function connectProformaToPortcall() {
  try {
    isSaving.value = true;
    if (props.callId && selectedImportProformaIds.value.length === 1) {
      const result = await connectProformaPortcall(
        props.callId,
        selectedImportProformaIds.value[0],
        callDetails.value.SEDNA_JOB_ID
      );
      if (
        result.resultCategory === GatResponseResultCategory.Warning ||
        result.resultCategory === GatResponseResultCategory.Error
      ) {
        window.messageBus.$emit('showMessage', {
          type: result.resultCategory === GatResponseResultCategory.Warning ? 'warning' : 'error',
          text: result.message || 'A problem occurred while connecting the proforma to portcall.',
          icon: true,
        });
      } else {
        await getProformas(props.callId);
      }
    }
  } finally {
    isSaving.value = false;
    connectProformaPortcallDialogVisible.value = false;
  }
}

async function prepareExportProformaToExpense() {
  try {
    isLoading.value = true;
    selectedExportProformaLinesId.value = [];
    const result = await apiGet(`proforma/${selectedProformaId.value}/proforma-expense-lines`);
    if (result.resultCategory === GatResponseResultCategory.Success && result.payload.length > 0) {
      exportableProformaLines.value = [...result.payload];
    }
    exportProformaToExpenseDialogVisible.value = true;
    await nextTick();
    selectedExportProformaLinesId.value = exportableProformaLines.value
      .filter((line) => line.EXPORTABLE === 1 && !!line.AMOUNT)
      .map((line) => line.ID);
  } catch {
    exportProformaToExpenseDialogVisible.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function exportProformaToExpense() {
  try {
    isSaving.value = true;
    if (
      selectedProformaId.value &&
      selectedExportProformaLinesId.value.length > 0 &&
      exportableProformaLines.value.length > 0
    ) {
      const exportableLines = exportableProformaLines.value.filter(
        (line) =>
          selectedExportProformaLinesId.value.includes(line.ID) &&
          line.PROFORMA_ID === selectedProformaId.value &&
          line.EXPORTABLE === 1
      );
      if (exportableLines.length > 0) {
        const linesWithHistory = exportableLines.map((line: ProformaExpenseLine) => {
          GlobalHelperFunctions.setHistoryField(line, 'HISTORY');
          return line;
        });
        const result = await apiPost(`proforma/${selectedProformaId.value}/create-expense`, linesWithHistory);
        if (result.resultCategory === GatResponseResultCategory.Success) {
          window.messageBus.$emit('showMessage', {
            type: 'success',
            text: `${result.payload?.successfulImports ?? 0} proforma line(s) successfully exported to expenses.`,
            icon: true,
          });
        } else if (
          result.resultCategory === GatResponseResultCategory.Warning ||
          result.resultCategory === GatResponseResultCategory.Error
        ) {
          window.messageBus.$emit('showMessage', {
            type: result.resultCategory === GatResponseResultCategory.Warning ? 'warning' : 'error',
            text: result.message || 'An error occurred while importing proforma to expense.',
            icon: true,
          });
        }
      }
    }
  } finally {
    isSaving.value = false;
    exportProformaToExpenseDialogVisible.value = false;
  }
}

function createEmailClick(item: ProformaEmailTemplate) {
  if (props.callId) {
    const guid = GlobalHelperFunctions.GUID();
    generateDocument('portcall', props.callId, item.ID, guid, 1, selectedProformaId.value).then((result) => {
      if (
        result.resultCategory === GatResponseResultCategory.Info &&
        result.resultType === GatResponseResultType.Custom
      ) {
        emailTemplateGuid.value = guid;
      }
    });
  }
}

watch(
  () => props.callId,
  (newValue) => {
    if (newValue) {
      getProformas(newValue);
    }
  },
  { immediate: true }
);

return { __sfc: true,props, proformaStore, appStatusStore, applicationStore, portcallStore, helperStore, isLoading, isSaving, agencyProformas, portcallProformas, exportableProformaLines, connectProformaPortcallDialogVisible, exportProformaToExpenseDialogVisible, selectedProformaIds, selectedImportProformaIds, selectedExportProformaLinesId, callDetails, emailTemplateGuid, agencyId, selectedProformaHasClientNumber, emailTemplateItems, validPortcallStatusToImportExportProforma, userCanConnectProformaPortcall, userCanExportProforma, userCanGenerateDoc, selectedProformaId, applicationStatus, wmButtonGroup, columns, newProformaColumns, proformaLineColumns, emailGeneratorIsLoading, getProformas, proformaSelected, importProformaRowClass, exportProformaLineRowClass, importProformaSelected, exportableProformaLineSelected, proformaClicked, checkboxIsHidden, prepareProformaPortcallConnect, connectProformaToPortcall, prepareExportProformaToExpense, exportProformaToExpense, createEmailClick, WmButtonGroup, EmailGenerator }
}

})